import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
    Alert,
    Anchor,
    Body1,
    Box,
    Button,
    Heading1,
    Heading2,
    HorizontalDivider,
    IconCheckCircle,
    Stack,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { type FragmentsOf, getFragment } from '../../graphql';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    type GetOfferQuery,
    PrescriberOrderStatus,
} from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';
import { getAbsoluteRoute } from '../../util';
import { getShippingMethodFromOffer } from '../../util/offers/get-shipping-method';
import { hasPHX502 } from '../../util/offers/sort';
import { shouldShowMemberCard } from '../../util/patient/should-show-member-card';
import { ContactUsWidget } from '../common/ContactUsWidget';
import { LocationInfo } from '../common/LocationInfo';
import { PharmacyDoesNotCarryMedicationAlert } from '../common/PharmacyDoesNotCarryMedicationAlert';
import { PrescriptionCancelledAlert } from '../common/PrescriptionCancelledAlert';
import { Pricing } from '../common/pricing';
import { Prescriber } from '../prescriber/Prescriber';
import { DAWModal } from '../preview-details/components/DAWModal';
import { ConditionalComponent } from '../utility/ConditionalComponent';

import { CancelledNextSteps } from './components/CancelledNextSteps';
import { NextSteps } from './components/NextSteps';
import { PrescriptionDetails } from './components/PrescriptionDetails';
import { ShareWithPharmacy } from './components/ShareWithPharmacy';

export type OrderConfirmationProps = {
    offer: NonNullable<FragmentsOf<GetOfferQuery['offer']>>;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

export const OrderConfirmation = ({
    offer,
    prescriberOrder,
}: OrderConfirmationProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { enableRxCancelMyphx } = useFlags();

    const [isDAWModalOpen, { close: closeDAWModal, toggle: toggleDAWModal }] =
        useDisclosure(false);

    const brand = searchParams.get('brand');
    const generic = searchParams.get('generic');
    const genericName = searchParams.get('genericName');
    const brandName = searchParams.get('brandName');

    const fillLocation = getFragment(offer.pharmacyProvider);
    const pharmacyDoesNotCarryMedication = hasPHX502(offer);
    const shippingMethod = pharmacyDoesNotCarryMedication
        ? null
        : getShippingMethodFromOffer(offer);

    const controlledSubstanceCode = getFragment(
        prescriberOrder.authorizedMedication
    ).controlledSubstanceCode;
    const isPending = prescriberOrder.status === PrescriberOrderStatus.Pending;
    const showMemberCard = shouldShowMemberCard(
        fillLocation?.name,
        offer.__typename === 'InsuranceOffer'
    );

    // Temporary until VELA integration
    const isInstantRouting = false;

    const isDAW = prescriberOrder.dispenseAsWritten;

    const shouldShowShareWithPharmacy =
        (isInstantRouting || !isPending || !controlledSubstanceCode) &&
        showMemberCard &&
        !pharmacyDoesNotCarryMedication;

    const isCancelled =
        prescriberOrder.status === PrescriberOrderStatus.Cancelled;
    const prescriber = getFragment(prescriberOrder.prescriber);
    const prescriberPhone = prescriber?.phoneNumber;

    return (
        <>
            <Stack gap="lg" pb="md">
                {controlledSubstanceCode && isPending && !isInstantRouting ? (
                    <Stack gap="sm">
                        <Heading1>
                            {t('orderConfirmation.inProgress.header')}
                        </Heading1>
                        <Body1>
                            {t('orderConfirmation.inProgress.description1')}
                        </Body1>
                        <Subtitle2>
                            {t('orderConfirmation.inProgress.description2')}
                        </Subtitle2>
                    </Stack>
                ) : (
                    <Stack gap="md">
                        <Stack align="center" gap="xxs">
                            <IconCheckCircle
                                filled
                                variant="rounded"
                                width="2em"
                                height="2em"
                                color="success"
                            />
                            <Heading1>{t('orderConfirmation.header')}</Heading1>
                        </Stack>
                        <PrescriptionCancelledAlert
                            prescriberOrder={prescriberOrder}
                        />
                        {isCancelled && enableRxCancelMyphx ? (
                            <CancelledNextSteps
                                prescriberPhone={prescriberPhone}
                            />
                        ) : pharmacyDoesNotCarryMedication ? (
                            <PharmacyDoesNotCarryMedicationAlert />
                        ) : (
                            <NextSteps location={fillLocation} />
                        )}
                        {isDAW && (
                            <Alert severity="info">
                                <Stack gap="xxs">
                                    <Subtitle2>
                                        {t(
                                            'genericSwap.orderConfirmation.daw.heading'
                                        )}
                                    </Subtitle2>
                                    <Box>
                                        <Trans i18nKey="genericSwap.orderConfirmation.daw.body">
                                            <Body1>
                                                {
                                                    'Generic versions can often be cheaper than brand name drugs. However, the healthcare provider flagged this prescription as '
                                                }
                                                <Anchor
                                                    onClick={toggleDAWModal}
                                                >
                                                    Dispense As Written
                                                </Anchor>
                                                {
                                                    ' for the brand. If the generic is preferred, contact the provider or pharmacy.'
                                                }
                                            </Body1>
                                        </Trans>
                                    </Box>
                                </Stack>
                            </Alert>
                        )}
                        {brand && !isDAW && (
                            <Alert severity="info">
                                <Body1>
                                    {t(
                                        'genericSwap.orderConfirmation.askForBrand'
                                    )}
                                </Body1>
                            </Alert>
                        )}
                        {generic && (
                            <Alert severity="info">
                                <Body1>
                                    {t(
                                        'genericSwap.orderConfirmation.askForGeneric'
                                    )}
                                </Body1>
                            </Alert>
                        )}
                    </Stack>
                )}

                <HorizontalDivider variant="full" />

                <Stack gap="xxl">
                    <Stack gap="sm">
                        <Heading2>
                            {t('orderConfirmation.pharmacy.subHeader')}
                        </Heading2>
                        <ConditionalComponent condition={!!fillLocation}>
                            <LocationInfo
                                location={fillLocation}
                                shippingMethod={shippingMethod}
                            />
                        </ConditionalComponent>
                    </Stack>
                    <ConditionalComponent
                        condition={shouldShowShareWithPharmacy}
                    >
                        <ShareWithPharmacy
                            offer={offer}
                            prescriberOrder={prescriberOrder}
                        />
                    </ConditionalComponent>

                    <PrescriptionDetails
                        controlledSubstanceCode={controlledSubstanceCode}
                        isInstantRouting={isInstantRouting}
                        offer={offer}
                        prescriberOrder={prescriberOrder}
                    />

                    <ConditionalComponent
                        condition={!pharmacyDoesNotCarryMedication}
                    >
                        <Pricing
                            offer={offer}
                            genericSwap={
                                generic
                                    ? {
                                          genericName: genericName!,
                                          brandName: brandName!,
                                      }
                                    : null
                            }
                        />
                    </ConditionalComponent>

                    <Stack gap="sm">
                        <Heading2>
                            {t('orderConfirmation.prescriber.subHeader')}
                        </Heading2>

                        <Body1>
                            {t('orderConfirmation.prescriber.description')}
                        </Body1>

                        <Prescriber
                            prescriber={getFragment(prescriberOrder.prescriber)}
                        />
                    </Stack>
                </Stack>

                <HorizontalDivider variant="full" />

                <ContactUsWidget />

                <Button
                    onClick={() => {
                        navigate(getAbsoluteRoute('home'));
                    }}
                >
                    {t('common.button.returnToHome')}
                </Button>
            </Stack>
            <DAWModal close={closeDAWModal} isOpen={isDAWModalOpen} />
        </>
    );
};

export default OrderConfirmation;
